import { createSelector } from '@reduxjs/toolkit';
import { get, isNil, map, pick } from 'lodash/fp';
import { QueryState } from 'app/lib/constants';
import { selectColumnAttributes } from 'app/store/attributes/selectors.js';
import { selectDatasetColumnFilterForPropFqn } from 'app/store/columnFilter/selectors';
import { selectQuerySchemaForPropFQN } from 'app/store/snowflakeQueries/selectors';
import { applySchemaFilter } from 'app/store/transformers.js';
import { getColumnPropertiesFromColumnAttributes } from './utils.js';

const pickMetadataFields = pick(['fqn', 'requestId', 'state', 'startedAt', 'finishedAt', 'error']);

const selectSlice = get('findDatasets');

export const selectCurrentFqn = createSelector(selectSlice, get('selectedFqn'));
export const selectStaleFqn = createSelector(selectSlice, get('staleFqn'));
export const selectCurrentTab = createSelector(selectSlice, get('selectedTab'));

const selectColumns = createSelector(selectSlice, get('columns'));
const selectColumnsProperties = createSelector(selectColumns, get('properties'));

export const selectIsLoadingColumns = createSelector(
    selectColumns,
    ({ state, metricsState }) => state === QueryState.RUNNING || metricsState === QueryState.RUNNING
);

export const selectColumnsMetadata = createSelector(selectColumns, pickMetadataFields);

export const selectColumnSchema = createSelector(selectColumns, get('schema'));
export const selectColumnNames = createSelector(selectColumnSchema, map('name'));

export const selectFilteredColumnSchema = createSelector(
    selectColumnSchema,
    selectDatasetColumnFilterForPropFqn,
    applySchemaFilter
);

export const selectColumnData = createSelector(
    selectColumns,
    selectColumnAttributes,
    selectDatasetColumnFilterForPropFqn,
    (columns, columnAttributes, columnFilter) => {
        const { fqn, schema, properties } = columns;

        if (isNil(schema)) {
            return null;
        }

        return applySchemaFilter(schema, columnFilter).map((column) => {
            const { name, typeFamily } = column;
            const columnFqn = { ...fqn, column: name };
            const description = properties?.[name]?.description ?? null;

            return [
                columnFqn,
                name,
                typeFamily,
                description,
                ...columnAttributes.map(({ uid }) => properties?.[name]?.[uid] ?? null)
            ];
        });
    }
);

export const selectPreviewSchema = createSelector(
    selectQuerySchemaForPropFQN,
    selectDatasetColumnFilterForPropFqn,
    selectColumnsProperties,
    selectColumnAttributes,
    (querySchema, columnFilter, columnsProperties, columnAttributes) => {
        const schema = applySchemaFilter(querySchema, columnFilter);

        if (isNil(schema)) {
            return null;
        }

        if (!isNil(columnsProperties)) {
            return schema.map((column) => {
                const columnProperties = columnsProperties[column.name];
                const { description, properties } = getColumnPropertiesFromColumnAttributes(
                    columnProperties,
                    columnAttributes
                );

                return {
                    ...column,
                    description,
                    properties
                };
            });
        }

        return schema;
    }
);
